import { useState, useEffect } from 'react';

import VectorFocusService from "../services/VectorFocusService";
import DenounceStatusService from "../services/DenounceStatusService";

import DataController from '../data/DataController';

import LogUtils from "../utils/LogUtils";

const TAG = "useVectorFocusService";
const logUtils = new LogUtils();

export function useGetVectorFocus({ id }) {
  const [isMounted, setIsMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(null);
  const [vectorFocus, setVectorFocus] = useState([]);

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
      setVectorFocus(null);
    };
  }, []);

  useEffect(() => {
    const vectorFocusService = new VectorFocusService();

    async function getVectorFocus({ id }) {
      try {
        if (!isMounted) return false;

        setIsLoading(true);
        const response = await vectorFocusService.getVectorFocus(id);
        setIsLoading(false);

        if (!isMounted || !response?.data) return false;

        setVectorFocus(response.data);

      } catch (err) {
        logUtils.logErrorMsg(TAG, "Failed to getVectorFocus", err);
        setIsLoading(false);
      }
    }

    getVectorFocus({ id });

    return () => {
      setVectorFocus(null);
    };
  }, [isMounted, id]);

  return [isLoading, vectorFocus];
}

export function useGetUserVectorFoci({ userId }) {
  const [isMounted, setIsMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(null);
  const [userVectorFoci, setUserVectorFoci] = useState([]);

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
      setUserVectorFoci(null);
    };
  }, []);

  useEffect(() => {
    const vectorFocusService = new VectorFocusService();
    const denounceStatusService = new DenounceStatusService();

    async function getUserVectorFoci({ userId }) {
      try {
        if (!isMounted) return false;

        setIsLoading(true);
        const response = await vectorFocusService.getUserVectorFocus(userId);
        const responseDenounceStatus = await denounceStatusService.getDenounceStatus();
        setIsLoading(false);

        if (!isMounted || !response?.data) return false;

        setUserVectorFoci(getVectorFocusWithStatus(responseDenounceStatus?.data, response.data));

      } catch (err) {
        logUtils.logErrorMsg(TAG, "Failed to getUserVectorFoci", err);
        setIsLoading(false);
      }
    }

    function getVectorFocusWithStatus(denounceStatus, vectorFoci) {
      if (!denounceStatus) return vectorFoci;

      return vectorFoci.map(vectorFocus => {
        denounceStatus.forEach(status => {
          if (vectorFocus.denounce_status_id === status.id) {
            vectorFocus.denounce_status = status;
          }
        });
        return vectorFocus;
      });
    }

    getUserVectorFoci({ userId });

    return () => {
      setUserVectorFoci(null);
    };
  }, [isMounted, userId]);

  return [isLoading, userVectorFoci];
}

export function useGetCityVectorFoci({ cityId, reload }) {
  const [isMounted, setIsMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(null);
  const [cityVectorFoci, setCityVectorFoci] = useState([]);

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
      setCityVectorFoci(null);
    };
  }, []);

  useEffect(() => {
    const vectorFocusService = new VectorFocusService();

    async function getCityVectorFoci({ cityId }) {
      try {
        if (!isMounted || !cityId) return false;

        setIsLoading(true);
        const dataController = new DataController();
        const filters = dataController.getFilter();
        const response = await vectorFocusService.getCityVectorFocusListWithFilters({
          cityId: cityId,
          filters: filters
        });
        setIsLoading(false);

        if (!isMounted || !response?.data) return false;

        setCityVectorFoci(response.data);

      } catch (err) {
        logUtils.logErrorMsg(TAG, "Failed to getCityVectorFoci", err);
        setIsLoading(false);
      }
    }

    getCityVectorFoci({ cityId });

    return () => {
      setCityVectorFoci(null);
    };
  }, [isMounted, cityId, reload]);

  return [isLoading, cityVectorFoci];
}
